import type { ComponentType } from "react";

export type TIntegrationIconComponent = ComponentType<{
  className?: string;
  role?: string;
  "aria-label"?: string;
  "aria-hidden"?: boolean;
}>;

/**
 * NOTE: NSAI-5354 — MCP HTTP transports the platform can speak.
 *
 * `SSE` is the legacy two-endpoint HTTP+SSE transport; `HTTP` is Streamable HTTP,
 * the current MCP spec transport. `AUTO` lets the server probe Streamable HTTP
 * then SSE and store whichever answered, which is the default because a growing
 * share of third-party servers ship Streamable HTTP only.
 *
 * Mirrors EMcpTransport in the server's src/integration/mcps/mcps.types.ts —
 * these two must stay in step.
 */
export enum EMcpTransport {
  AUTO = "auto",
  SSE = "sse",
  HTTP = "http",
}

/**
 * NOTE: NSAI-5354 — how the platform authenticates to an MCP server. Every scheme
 * is a static credential turned into request headers.
 *
 * Mirrors EMcpAuthMode in the server's src/integration/mcps/mcps.types.ts.
 */
export enum EMcpAuthMode {
  NONE = "none",
  BEARER = "bearer",
  // NOTE: Arbitrary header name, e.g. `X-API-Key: <token>`.
  HEADER = "header",
  // NOTE: The token is the COMPLETE Authorization value, e.g. `Token abc123`.
  RAW = "raw",
}

export enum EIntegrationProvider {
  AIRTABLE = "airtable",
  CALENDLY = "calendly",
  ATLASSIAN_CONFLUENCE = "atlassian-confluence",
  ATLASSIAN_CONFLUENCE_DC = "atlassian-confluence-dc",
  ATLASSIAN_JIRA = "atlassian-jira",
  ATLASSIAN_JIRA_DC = "atlassian-jira-dc",
  AZURE_OUTLOOK = "azure-outlook",
  AZURE_CALENDAR = "azure-calendar",
  AZURE_ONEDRIVE = "azure-onedrive",
  AZURE_SHARE_POINT = "azure-share-point",
  AZURE_EXCEL = "azure-excel",
  AZURE_MICROSOFT_TEAMS = "azure-microsoft-teams",
  AZURE_MES = "azure-mes",
  GITHUB = "github",
  GITLAB = "gitlab",
  GOOGLE_DRIVE = "google-drive",
  GOOGLE_SHEETS = "google-sheets",
  GOOGLE_CALENDAR = "google-calendar",
  GOOGLE_MEET = "google-meet",
  GOOGLE_MAIL = "google-mail",
  HUBSPOT = "hubspot",
  LINEAR = "linear",
  NOTION = "notion",
  ZENDESK = "zendesk",
  SLACK = "slack",
  TODOIST = "todoist",
  ASANA = "asana",
  BITRIX24 = "bitrix24",
  FIREFLIES = "fireflies",
  CROWDSTRIKE = "crowdstrike",
  KONTUR_DIADOC = "kontur-diadoc",
  KONTUR_FOCUS = "kontur-focus",
  MCPS = "mcps",
  PDF_GENERATOR = "pdf-generator",
  DOCX_GENERATOR = "docx-generator",
  TELEGRAM = "telegram",
  BAMBOOHR = "bamboohr",
  TRELLO = "trello",
  DROPBOX = "dropbox",
  ZOOM = "zoom",
  YANDEX = "yandex",
  NEXTCLOUD = "nextcloud",
  // NOTE: Pipedream-only providers (no native or Composio rail).
  SMARTSHEET = "smartsheet",
  DATABRICKS = "databricks",
  ONEPASSWORD = "_1password",
  TWENTYONE_RISK = "_21risk",
  TWOCHAT = "_2chat",
  ABLY = "ably",
  ABSTRACT = "abstract",
  ABUSE_IPDB = "abuselpdb",
  ABYSSALE = "abyssale",
  ACCREDIBLE_CERTIFICATES = "accredible_certificates",
  ACCULYNX = "acculynx",
  ACTIVE_CAMPAIGN = "active_campaign",
  ACTIVETRAIL = "active_trail",
  ADDRESSZEN = "addresszen",
  ADRAPID = "adrapid",
  ADYNTEL = "adyntel",
  AEROLEADS = "aeroleads",
  AFFINDA = "affinda",
  AFFINITY = "affinity",
  AGENCYZOOM = "agencyzoom",
  AGENT_MAIL = "agent_mail",
  AGENTQL = "agentql",
  AGENTY = "agenty",
  AGILED = "agiled",
  AGILITY_CMS = "agility_cms",
  AHREFS = "ahrefs",
  AI_ML_API = "ai_ml_api",
  AIVOOV = "aivoov",
  ALCHEMY = "alchemy",
  ALGODOCS = "algodocs",
  ALGOLIA = "algolia",
  ALL_IMAGES_AI = "all_images_ai",
  ALPHA_VANTAGE = "alpha_vantage",
  ALTOVIZ = "altoviz",
  ALTTEXT_AI = "alttext_ai",
  AMARA = "amara",
  AMBEE = "ambee",
  AMBIENT_WEATHER = "ambient_weather",
  AMCARDS = "amcards",
  AMPLITUDE = "amplitude",
  ANCHOR_BROWSER = "anchor_browser",
  ANONYFLOW = "anonyflow",
  ANTHROPIC_ADMINISTRATOR = "anthropic_administrator",
  API_LABZ = "api_labz",
  API_NINJAS = "api_ninjas",
  API_SPORTS = "api_sports",
  API_BIBLE = "api_bible",
  API2PDF = "api2pdf",
  APIFLASH = "apiflash",
  APIFY = "apify",
  APILIO = "apilio",
  APIPIE_AI = "apipie_ai",
  APIVERVE = "apiverve",
  APOLLO = "apollo",
  APPCIRCLE = "appcircle",
  APPDRAG = "appdrag",
  APPOINTO = "appointo",
  APPVEYOR = "appveyor",
  ARYN = "aryn",
  ASCORA = "ascora",
  ASHBY = "ashby",
  ASIN_DATA_API = "asin_data_api",
  ASTICA_AI = "astica_ai",
  ASYNC_INTERVIEW = "async_interview",
  AUTOBOUND = "autobound",
  AUTOM = "autom",
  AYRSHARE = "ayrshare",
  BANNERBEAR = "bannerbear",
  BART = "bart",
  BASEROW = "baserow",
  BASIN = "basin",
  BEACONCHA_IN = "beaconchain",
  BEACONSTAC = "beaconstac",
  BEEMINDER = "beeminder",
  BENCHMARK_EMAIL = "benchmark_email",
  BENZINGA = "benzinga",
  BESTBUY = "bestbuy",
  BETTER_PROPOSALS = "better_proposals",
  BETTER_STACK = "better_stack",
  BETTERCONTACT = "bettercontact",
  BIDSKETCH = "bidsketch",
  BIGDATACLOUD = "big_data_cloud",
  BIGMAILER = "bigmailer",
  BIGML = "bigml",
  BIGPICTURE_IO = "bigpicture_io",
  BITQUERY = "bitquery",
  BLACKBAUD = "blackbaud",
  BLAZEMETER = "blazemeter",
  BLOCKNATIVE = "blocknative",
  BOLNA = "bolna",
  BOLOFORMS = "boloforms",
  BOLT_IOT = "bolt_iot",
  BONSAI = "bonsai",
  BOOKINGMOOD = "bookingmood",
  BOTPRESS = "botpress",
  BOUNCER = "bouncer",
  BOX = "box",
  BOXHERO = "boxhero",
  BRANDFETCH = "brandfetch",
  BREATHE_HR = "breathehr",
  BREEZE = "breeze",
  BRIGHT_DATA = "brightdata",
  BRILLIANT_DIRECTORIES = "brilliant_directories",
  BROWSEAI = "browseai",
  APALEO = "apaleo",
  APIFY_MCP = "apify_mcp",
  ATTIO = "attio",
  BASECAMP = "basecamp",
  BITWARDEN = "bitwarden",
  CAL = "cal",
  CANVA = "canva",
  CANVAS = "canvas",
  CAPSULE_CRM = "capsule_crm",
  CLICKUP = "clickup",
  CLOCKIFY = "clockify",
  CONTENTFUL = "contentful",
  CROWDIN = "crowdin",
  DART = "dart",
  DATADOG = "datadog",
  DIALPAD = "dialpad",
  DIGITAL_OCEAN = "digital_ocean",
  DISCORD_BOT = "discordbot",
  DOCUSIGN = "docusign",
  DROPBOX_SIGN = "dropbox_sign",
  DUB = "dub",
  EPIC_GAMES = "epic_games",
  EVENTBRITE = "eventbrite",
  EXIST = "exist",
  FACEBOOK = "facebook",
  FATHOM = "fathom",
  FIGMA = "figma",
  FLY = "fly",
  FOLLOW_UP_BOSS = "follow_up_boss",
  FREEAGENT = "freeagent",
  FRESHBOOKS = "freshbooks",
  GONG = "gong",
  GOOGLE_ADMIN = "google_admin",
  GOOGLE_ADS = "googleads",
  GOOGLE_ANALYTICS = "google_analytics",
  GOOGLE_BIGQUERY = "googlebigquery",
  GOOGLE_CLASSROOM = "google_classroom",
  GOOGLE_DOCS = "googledocs",
  GOOGLE_PHOTOS = "googlephotos",
  GOOGLE_SEARCH_CONSOLE = "google_search_console",
  GOOGLE_SLIDES = "googleslides",
  GOOGLE_SUPER = "googlesuper",
  GOOGLE_TASKS = "googletasks",
  GUMROAD = "gumroad",
  HARVEST = "harvest",
  HELP_SCOUT = "help_scout",
  HUGGING_FACE = "hugging_face",
  INSIGHTO_AI = "insighto_ai",
  INSTAGRAM = "instagram",
  INTERCOM = "intercom",
  KIT = "kit",
  KLAVIYO = "klaviyo",
  LEVER = "lever",
  LINKEDIN = "linkedin",
  LINKEDIN_ADS = "linkedin_ads",
  LINKHUT = "linkhut",
  LODGIFY = "lodgify",
  MAILCHIMP = "mailchimp",
  MATTERPORT = "matterport",
  META_ADS = "metaads",
  MIRO = "miro",
  MONDAY = "monday",
  MONDAY_MCP = "monday_mcp",
  MONEYBIRD = "moneybird",
  MURAL = "mural",
  ODOO = "odoo",
  OMNISEND = "omnisend",
  PAGERDUTY = "pagerduty",
  PARMA = "parma",
  PINECONE = "pinecone",
  PIPEDRIVE = "pipedrive",
  PRISMA = "prisma",
  PRODUCTBOARD = "productboard",
  PUSHBULLET = "pushbullet",
  QUICKBOOKS = "quickbooks",
  RAMP = "ramp",
  REDDIT = "reddit",
  REDDIT_ADS = "reddit_ads",
  ROAM = "roam",
  BOLDSIGN = "boldsign",
  BORNEO = "borneo",
  BREVO = "brevo",
  BREX = "brex",
  BITBUCKET = "bitbucket",
  DISCORD = "discord",
  SUPABASE = "supabase",
  YOUTUBE = "youtube",
  WEBEX = "webex",
  WAKATIME = "wakatime",
  TYPEFORM = "typeform",
  TIMELY = "timely",
  TICKTICK = "ticktick",
  STRAVA = "strava",
  STACK_EXCHANGE = "stack_exchange",
  SQUARE = "square",
  SPLITWISE = "splitwise",
  SENTRY = "sentry",
  ONENOTE = "onenote",
  WORKABLE = "workable",
  // NOTE: NSAI-6018 — kept in step with the server enum
  // (apps/nsai/server/src/integration/integration.types.ts). Values are the
  // provider ids, not the Pipedream app slugs.
  MICROSOFT_PLANNER = "microsoft-planner",
  MICROSOFT_TODO = "microsoft-todo",
  MICROSOFT_BOOKINGS = "microsoft-bookings",
  // NOTE: NSAI-6973 — kept in step with the server enum.
  ZOHO_CRM = "zoho-crm",
  // NOTE: NSAI-7339 — kept in step with the server enum.
  ZOHO_DESK = "zoho-desk",
  ZOHO_MAIL = "zoho-mail",
  ZOHO_ANALYTICS = "zoho-analytics",
  ZOHO_WORKDRIVE = "zoho-workdrive",
}

// NOTE: Data Center providers are included — they connect through the same
// OAuth access-url endpoint and IConnectIntegrationPayload.integrationType
// path as the cloud Atlassian providers.
export type TAtlassianIntegrationProvider =
  | EIntegrationProvider.ATLASSIAN_CONFLUENCE
  | EIntegrationProvider.ATLASSIAN_CONFLUENCE_DC
  | EIntegrationProvider.ATLASSIAN_JIRA
  | EIntegrationProvider.ATLASSIAN_JIRA_DC;

export type TAzureIntegrationProvider =
  | EIntegrationProvider.AZURE_OUTLOOK
  | EIntegrationProvider.AZURE_CALENDAR
  | EIntegrationProvider.AZURE_ONEDRIVE
  | EIntegrationProvider.AZURE_SHARE_POINT
  | EIntegrationProvider.AZURE_EXCEL
  | EIntegrationProvider.AZURE_MICROSOFT_TEAMS
  | EIntegrationProvider.AZURE_MES;

export type TGoogleIntegrationProvider =
  | EIntegrationProvider.GOOGLE_CALENDAR
  | EIntegrationProvider.GOOGLE_DRIVE
  | EIntegrationProvider.GOOGLE_MAIL
  | EIntegrationProvider.GOOGLE_MEET
  | EIntegrationProvider.GOOGLE_SHEETS;

export type TDropboxIntegrationProvider = EIntegrationProvider.DROPBOX;

// NOTE: Server-computed UI hints per provider — mirrors the server-side
// IEnabledIntegrationProviderInfo. Lets the apps render the connect flow
// (direct OAuth / config modal / connector picker) without re-deriving
// routing logic on every client.
export interface IEnabledIntegrationProviderInfo {
  provider: EIntegrationProvider;
  hasComposioConnector: boolean;
  hasNodeshiftConnector: boolean;
  // NOTE: True when the provider is reachable through Pipedream Connect.
  hasPipedreamConnector: boolean;
  // NOTE: True when more than one connector is present — open SelectConnectorModal
  // before initiating the connect call.
  showConnectorPicker: boolean;
  // NOTE: True when the connect flow needs a provider-specific config modal
  // (API key, EWS URL, domain, ...) before OAuth. The app looks up which
  // specific modal to render via COMPOSIO_INTEGRATION_DEFINITIONS[].modalKey.
  requiresConfigModal: boolean;
  // NOTE: Only set for Atlassian Data Center providers. `false` means the
  // current project's organization has not configured that DC product, so the
  // connect card is hidden; the provider still appears in `enabled` (deployment
  // level) so the org owner's settings form can show the empty config section.
  // Undefined for every other provider (never hidden).
  isOrgConfigured?: boolean;
}

export interface IEnabledIntegrationsResponse {
  enabled: IEnabledIntegrationProviderInfo[];
  enabledFF: IEnabledIntegrationProviderInfo[];
}

export interface IStaticIntegration {
  id: EIntegrationProvider;
  name: string;
  icon: TIntegrationIconComponent;
  description: string;
  provider: EIntegrationProvider | null;
  onConnect: () => void;
  isConnecting: boolean;
}

// NOTE: Mirrors server-side EConnectorType (apps/nsai/server/src/integration/integration.types.ts).
// Identifies which connector implementation owns an integration record.
export enum EConnectorType {
  INTERNAL = "INTERNAL",
  COMPOSIO = "COMPOSIO",
  PIPEDREAM = "PIPEDREAM",
}

// NOTE: Mirrors server-side IIntegrationProviderConfig — the UI consumes this to
// decide whether to render a connector-selection modal (more than one connector
// available) or to route directly into whichever connector is available.
export interface IIntegrationProviderConfig {
  composioMapKey: string | null;
  hasComposioConnector: boolean;
  hasNodeshiftConnector: boolean;
  hasPipedreamConnector?: boolean;
}

export interface IConnectedIntegration {
  uuid: string;
  title: string;
  provider: EIntegrationProvider;
  connectorType: EConnectorType;
  isExpired: boolean;
  createdAt: string;
  updatedAt: string;
  isOrganizationLevel?: boolean;
}

export interface ITool {
  title: string;
  description: string;
  parameters: Record<string, string>;
}

export interface IIntegrationMetadata {
  integrations: IConnectedIntegration[];
  tools: ITool[];
}

export type TParsedIntegrations = Record<
  EIntegrationProvider,
  IIntegrationMetadata
>;

export enum ECrowdstrikeRegion {
  US1 = "US1",
  US2 = "US2",
  EU1 = "EU1",
}

export interface IDriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  size: string;
  integrationUuid: string;
  // NOTE: Optional so Drive, OneDrive and Dropbox are unaffected. Only
  // providers with navigable folders set them — Nextcloud is the first, since
  // WebDAV is directory-scoped and has no flat listing.
  isFolder?: boolean;
  path?: string;
}

// NOTE: Which connect flow an organization allows. Mirrors the server enum;
// `both` (or an absent value) means the member may choose.
export enum ENextcloudAuthFlow {
  BOTH = "both",
  LOGIN_FLOW_V2 = "login_flow_v2",
  APP_PASSWORD = "app_password",
}

export interface INextcloudOrgConfig {
  baseUrl: string | null;
  authFlow: ENextcloudAuthFlow | null;
}

export interface IStartNextcloudLoginFlowResponse {
  // NOTE: Opaque server-side handle. Never the Nextcloud poll token.
  handle: string;
  loginUrl: string;
}

export enum ENextcloudLoginFlowStatus {
  PENDING = "pending",
  CONNECTED = "connected",
}

// NOTE: i18n keys rather than free text, so the caller translates and the
// typed `t()` accepts them. An enum rather than a string union, per the style
// rules.
export enum ENextcloudLoginFlowError {
  FAILED = "nextcloud.signInFailed",
  EXPIRED = "nextcloud.signInExpired",
}

export interface INextcloudLoginFlowStatusResponse {
  status: ENextcloudLoginFlowStatus;
  isNewIntegration?: boolean;
}

/**
 * NOTE: NSAI-5354 — which half of the MCP connect form is showing.
 *
 * The form used to be either/or: with any organization-managed server configured, the
 * member saw ONLY the picker and could not add a server of their own; with none, they
 * saw ONLY the manual fields and had no way to reach a managed one. Both halves now
 * live in one modal behind this switch (web and mobile alike).
 */
export enum EMcpConnectMode {
  ORGANIZATION = "organization",
  CUSTOM = "custom",
}

// NOTE: NSAI-5354 — one owner-managed MCP server as the connect form sees it. Named
// (it was inline on IIntegrationConfigs) so the managed-server lookup in
// utils/mcp.ts and both connect forms can refer to the same shape. Already
// project-scoped by the server: getIntegrationConfigs drops configs scoped to other
// projects, so anything here is a server this member may pick.
export interface IIntegrationConfigMcp {
  title: string;
  url: string;
  isRequiredToken: boolean;
  // NOTE: NSAI-5354 — the transport the org resolved for this server, so the connect
  // form can show it. AUTO for a config saved before the field existed.
  transport: EMcpTransport;
}

export interface IIntegrationConfigs {
  nextcloud: INextcloudOrgConfig;
  isBitrix24Enabled: boolean;
  isMesEnabled: boolean;
  isKonturFocusEnabled: boolean;
  isKonturDiadocEnabled: boolean;
  isKonturDiadocCredentialsConfigured: boolean;
  mcps: IIntegrationConfigMcp[];
}

export interface IConnectIntegrationPayload {
  redirectTo: string;
  provider: EIntegrationProvider;
  isExternalIntegrationsProviderEnabled?: boolean;
  ewsUrl?: string | undefined;
  ewsDomain?: string | undefined;
  ewsUsername?: string;
  ewsPassword?: string;
  apiKey?: string;
  integrationType?:
    | TAtlassianIntegrationProvider
    | TGoogleIntegrationProvider
    | TAzureIntegrationProvider
    | TDropboxIntegrationProvider;
  clientId?: string;
  clientSecret?: string;
  crowdstrikeRegion?: ECrowdstrikeRegion;
  mcpUuid?: string;
  mcpTitle?: string;
  mcpUrl?: string;
  mcpToken?: string;
  // NOTE: NSAI-5354 — transport pin. Omitted means AUTO: the server probes
  // Streamable HTTP then SSE and stores whichever answered.
  mcpTransport?: EMcpTransport;
  // NOTE: NSAI-5354 — auth scheme. Omitted resolves server-side to the legacy
  // behaviour (bearer when a token is present, nothing when it is not).
  mcpAuthMode?: EMcpAuthMode;
  // NOTE: Only meaningful for EMcpAuthMode.HEADER, e.g. "X-API-Key".
  mcpAuthHeaderName?: string;
  bamboohrCompanyDomain?: string;
  // NOTE: No server URL — Nextcloud is configured per organization, so the
  // member supplies only their own credentials.
  nextcloudLoginName?: string;
  nextcloudAppPassword?: string;
  // NOTE: Sent when the provider has BOTH connectors available. The server
  // routes the connect request through the chosen connector. Omit when only
  // one connector exists; the server then picks the default.
  connectorType?: EConnectorType;
}

export interface IConfluenceSpace {
  id: string;
  url: string;
  name: string;
  scopes: string[];
  avatarUrl: string;
  integrationUuid: string;
}

export interface IConfluencePage {
  url: string;
  content: {
    id: string;
    title: string;
  };
  integrationUuid: string;
}

export interface IConfluencePageListParams {
  spaceId?: string;
  integrationUuid?: string;
  pageSize?: number;
  pageNumber?: number;
  search?: string;
}

export interface IFileListParams {
  search?: string;
  pageSize?: number;
  mimeTypes?: string;
}

// NOTE: Nextcloud lists one directory at a time — WebDAV has no flat listing,
// so the picker walks the tree rather than requesting everything.
export interface INextcloudFileListParams extends IFileListParams {
  // NOTE: Required by the endpoint — a member may have connected more than one
  // Nextcloud account, and the row carries the server URL to talk to.
  integrationUuid?: string;
  path?: string;
}

export interface IIntegrationTestResponse {
  message: string;
}

export interface IIntegrationConnectResponse {
  url: string;
}

export interface IDeleteIntegrationConnectionPayload {
  integrationUuid: string;
  provider: EIntegrationProvider;
}

export interface ITestIntegrationConnectionPayload {
  integrationUuid: string;
  provider: EIntegrationProvider;
}

// NOTE: NSAI-4328 — Telegram's connect is a three-step MTProto login rather
// than an OAuth redirect: the server sends a code to the phone, verifies it,
// and asks for the account's 2FA password only when the account has one. The
// step the server answers with is what drives the next screen; `COMPLETE` means
// the connection exists. Web declared these locally in
// `web/src/api/integration/types.telegram.ts`; they live here so mobile and the
// extension consume the same contract.
export enum ETelegramAuthStep {
  PHONE = "phone",
  CODE = "code",
  PASSWORD = "password",
  COMPLETE = "complete",
}

export interface ITelegramAuthResponse {
  success: boolean;
  step: ETelegramAuthStep;
  message?: string;
  requiresPassword?: boolean;
}

export interface ITelegramSendCodePayload {
  phoneNumber: string;
}

export interface ITelegramVerifyCodePayload {
  phoneNumber: string;
  code: string;
}

export interface ITelegramVerifyPasswordPayload {
  phoneNumber: string;
  password: string;
}
